import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Transition } from 'react-transition-group';
import BackgroundImage from '@deliveroo/consumer-component-library/components/BackgroundImage';
import LoadingIndicator from '@deliveroo/consumer-component-library/components/LoadingIndicator';
import Modal from '@deliveroo/consumer-component-library/components/Modal';
import Spacer from '@deliveroo/consumer-component-library/components/Spacer';
import Span from '@deliveroo/consumer-component-library/components/Span';
import UnstyledButton from '@deliveroo/consumer-component-library/components/UnstyledButton';
import HomeTitle from '../../components/HomeTitle';
import MatchMedia from '../../areas/shared/components/MatchMedia';
import HomeMapCards from '../HomeMapCards';
import i18n from '../../utils/i18n';
import {
  requestHomeMapView,
  setHomeMapSelectedRestaurantId,
  toggleMapView,
} from '../../actions/homeMapActions';
import { trackToggledShowMapView } from '../../actions/tracking/homeMap';
import { getNeighborhood, getTitle, getLatLon } from '../../selectors/homeSelectors';
import {
  getHomeMapHasError,
  getHomeMapPins,
  getHomeMapSelectedRestaurantId,
  getIsHomeMapLoading,
} from '../../selectors/homeMapSelectors';
import { PinShape, LatLngShape } from '../HomePickupMap/propTypes';

import styles from './styles.scss';
import errorImageUrl from './images/location-unknown.svg';

const HomePickupMap = dynamic(import('../HomePickupMap'), { ssr: false });

const DEBOUNCE_DELAY = 300; // ms
const MOBILE_MAP_PADDING = {
  bottom: 120,
  left: 0,
  right: 0,
  top: 0,
};

class HomeMapLayout extends Component {
  state = {
    areCardsScrolling: false,
    showSearchButton: false,
  };

  mapInstance = null;

  scrollingContainerRef = React.createRef(null);

  scrollIntoView = (element, options) => {
    this.setState({ areCardsScrolling: true }, () => {
      element.scrollIntoView(options);
      // In browsers that support smooth scrolling (Chrome and
      // Firefox), we allow for enough time for the scrolling
      // animation to complete.
      setTimeout(() => {
        this.setState({ areCardsScrolling: false });
      }, 500);
    });
  };

  onMapInstantiated = (map) => {
    this.mapInstance = map;
  };

  onBoundsChanged = debounce(() => {
    this.setState({ showSearchButton: true });
  }, DEBOUNCE_DELAY);

  onSearchButtonClick = () => {
    const location = this.getSearchThisAreaLocation();
    this.props.updateResults(location);
    this.setState({ showSearchButton: false });
  };

  /**
   * Scroll listener to capture the restaurant ID corresponding to the card
   * that was last snapped into place by the user. This listener also gets
   * triggered after clicking on a restaurant pin since that calls the
   * `scrollIntoView` API on the matching card. As a result, we return early
   * when such programmatic scroll is happening.
   */
  onScroll = debounce(() => {
    if (this.state.areCardsScrolling) {
      return;
    }

    const scrollingContainer = this.scrollingContainerRef.current;

    if (!scrollingContainer) {
      return;
    }

    const cards = [...scrollingContainer.querySelectorAll('.home-map__card-spacer')];

    if (!cards.length) {
      return;
    }

    const { pins, selectedRestaurantId, setSelectedRestaurantId } = this.props;
    const xDeltas = cards.map((card) => card.offsetLeft - scrollingContainer.scrollLeft);
    const minDelta = Math.min(...xDeltas.filter((x) => x >= 0));

    if (xDeltas.includes(minDelta)) {
      const minDeltaIndex = xDeltas.indexOf(minDelta);
      const snappedCard = cards[minDeltaIndex];
      const { restaurantId } = snappedCard.dataset;

      if (restaurantId && restaurantId !== selectedRestaurantId) {
        const snappedPin = pins.find((pin) => pin.restaurantId === restaurantId);
        if (this.mapInstance && snappedPin) {
          this.mapInstance.panTo({
            lat: snappedPin.lat,
            lng: snappedPin.lon,
          });
        }
        setSelectedRestaurantId(restaurantId);
      }
    }
  }, DEBOUNCE_DELAY);

  getSearchThisAreaLocation = () => {
    const bounds = this.mapInstance.getBounds();
    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();
    const boundingBox = {
      maxLat: northEast.lat(),
      maxLon: northEast.lng(),
      minLat: southWest.lat(),
      minLon: southWest.lng(),
    };
    return {
      bounding_box: boundingBox,
      // Used by the backend to calculate the distance
      // between the user and the restaurants.
      ...this.props.userLatLon,
    };
  };

  render() {
    const { hasError, isLoading, neighborhood, onBackToList, title } = this.props;
    const { areCardsScrolling, showSearchButton } = this.state;

    const renderTopOverlay = () => (
      <Transition appear in={showSearchButton} timeout={200}>
        {(state) => (
          <div className={classnames(styles.modalTopOverlay, styles[state])}>
            <UnstyledButton onClick={this.onSearchButtonClick}>
              <span className={styles.modalButton}>
                <Span isBold color="teal">
                  {i18n.gettext('home.map_view.search_this_area')}
                </Span>
              </span>
            </UnstyledButton>
          </div>
        )}
      </Transition>
    );

    const renderErrorView = () => (
      <div className={styles.errorContainer}>
        <div className={styles.errorImage}>
          <BackgroundImage src={errorImageUrl} />
        </div>
        <Span adjacentVerticalSpacing="medium" isBold>
          {i18n.gettext('home.map_view.error_title')}
        </Span>
        <Span adjacentVerticalSpacing="small">{i18n.gettext('home.map_view.error_message')}</Span>
      </div>
    );

    const renderLoadingView = () => (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingIndicator}>
          <LoadingIndicator size={36} spacing="medium" />
        </div>
      </div>
    );

    return (
      <MatchMedia mobile>
        {(matches) =>
          matches ? (
            <Modal
              isOpen
              isContentEdgeToEdge
              appElement="#__next"
              type="fullscreen"
              headerTitle={neighborhood}
              onBack={onBackToList}
            >
              {(header) => (
                <div className={styles.modalBackground}>
                  {header}
                  <div className={styles.modalContent}>
                    {isLoading && renderLoadingView()}
                    {!isLoading && hasError && renderErrorView()}
                    {!isLoading &&
                      !hasError && (
                        <Fragment>
                          <HomePickupMap
                            padding={MOBILE_MAP_PADDING}
                            onBoundsChanged={this.onBoundsChanged}
                            onMapInstantiated={this.onMapInstantiated}
                          />
                          {renderTopOverlay()}
                          <div
                            ref={this.scrollingContainerRef}
                            className={styles.modalCards}
                            onScroll={this.onScroll}
                          >
                            <HomeMapCards
                              isScrolling={areCardsScrolling}
                              scrollingDirection="horizontal"
                              scrollIntoView={this.scrollIntoView}
                            />
                          </div>
                        </Fragment>
                      )}
                  </div>
                </div>
              )}
            </Modal>
          ) : (
            <Fragment>
              <div className={classnames(styles.xxlContainer, styles.homeTitleContainer)}>
                <Spacer spacing="large 0 medium 0">
                  <HomeTitle
                    title={title}
                    titleLink={i18n.gettext('home.map_view.back_to_restaurant_list')}
                    onClick={onBackToList}
                  />
                </Spacer>
              </div>
              <div className={classnames(styles.xxlContainer, styles.row)}>
                {isLoading && renderLoadingView()}
                {!isLoading && hasError && renderErrorView()}
                {!isLoading &&
                  !hasError && (
                    <Fragment>
                      <div className={styles.leftCol}>
                        <HomeMapCards
                          isScrolling={areCardsScrolling}
                          scrollingDirection="vertical"
                          scrollIntoView={this.scrollIntoView}
                        />
                      </div>
                      <div className={styles.rightCol}>
                        <div className={styles.desktopMapContainer}>
                          <HomePickupMap
                            hasZoomControls
                            onBoundsChanged={this.onBoundsChanged}
                            onMapInstantiated={this.onMapInstantiated}
                          />
                        </div>
                        {renderTopOverlay()}
                      </div>
                    </Fragment>
                  )}
              </div>
            </Fragment>
          )
        }
      </MatchMedia>
    );
  }
}

HomeMapLayout.propTypes = {
  hasError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  neighborhood: PropTypes.string.isRequired,
  onBackToList: PropTypes.func.isRequired,
  pins: PropTypes.arrayOf(PinShape).isRequired,
  selectedRestaurantId: PropTypes.string.isRequired,
  setSelectedRestaurantId: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  updateResults: PropTypes.func.isRequired,
  userLatLon: LatLngShape.isRequired,
};

const mapStateToProps = (state) => ({
  hasError: getHomeMapHasError(state),
  isLoading: getIsHomeMapLoading(state),
  neighborhood: getNeighborhood(state),
  pins: getHomeMapPins(state),
  selectedRestaurantId: getHomeMapSelectedRestaurantId(state),
  title: getTitle(state),
  userLatLon: getLatLon(state),
});

const mapDispatchToProps = (dispatch) => ({
  onBackToList: () => {
    dispatch(trackToggledShowMapView());
    dispatch(toggleMapView());
  },
  updateResults: (location) => dispatch(requestHomeMapView({ location })),
  setSelectedRestaurantId: (restaurantId) => dispatch(setHomeMapSelectedRestaurantId(restaurantId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HomeMapLayout);
